import {
	faChevronDown,
	faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalCategoryDto } from '@supply-mover-2-0/shared';
import {
	ResilientImage,
	Tooltip,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

interface CategoryTreeItemProps {
	category: PortalCategoryDto;
	/** The category row itself is the active selection (no subcategory chosen). */
	isSelected: boolean;
	/** This category owns the current selection, subcategory or not. */
	isSelectedBranch: boolean;
	selectedSubcategory: string;
	subcategoriesShown: boolean;
	onSelect: (category: string, subcategory?: string) => void;
	onToggleExpanded: () => void;
}

/**
 * One row of the catalog's category tree, plus its subcategory disclosure.
 *
 * Extracted from CatalogBrowser for PD-15471: the row needs per-row state
 * (whether its own label is currently ellipsized) to decide whether a
 * tooltip is worth showing, and hooks can't run inside the render loop the
 * list used to be.
 *
 * The layout is deliberately tight. The sidebar is 224px, so anything
 * standing between the left edge and the name — an icon slot, generous
 * padding, a long item count — comes straight out of the space the category
 * name has to render in, and the customer's complaint was that they could
 * not read the names. Hence: no placeholder icon, no item count, the row
 * bleeding into the card's own padding so names line up with the "Categories"
 * heading above them, and a narrow disclosure control.
 */
export function CategoryTreeItem({
	category,
	isSelected,
	isSelectedBranch,
	selectedSubcategory,
	subcategoriesShown,
	onSelect,
	onToggleExpanded,
}: CategoryTreeItemProps): JSX.Element {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const labelRef = useRef<HTMLSpanElement>(null);
	const [isLabelTruncated, setIsLabelTruncated] = useState(false);

	// Only offer the full name in a tooltip when the name is actually cut
	// off — a tooltip that just repeats a fully-visible label is noise.
	// Re-measured on resize (the sidebar is fluid below its breakpoint) and
	// once webfonts settle, since a font swap changes the rendered width
	// without changing the box the ResizeObserver watches.
	useEffect(() => {
		const element = labelRef.current;
		if (element == null) {
			return;
		}

		const measure = (): void => {
			// +1 absorbs sub-pixel rounding, which otherwise reports a
			// perfectly-fitting label as truncated.
			setIsLabelTruncated(element.scrollWidth > element.clientWidth + 1);
		};

		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(element);

		let cancelled = false;
		void document.fonts.ready.then(() => {
			if (!cancelled) {
				measure();
			}
		});

		return (): void => {
			cancelled = true;
			observer.disconnect();
		};
	}, [category.category]);

	const hasImage =
		category.imageUrl != null && category.imageUrl.trim().length > 0;

	return (
		<div>
			{/* Negative margin bleeds the row into the card's own padding, so
			    the category NAME lines up with the "Categories" heading while
			    the selected/hover highlight still reads as a full-width row. */}
			<div className="flex items-center -mx-2">
				<Tooltip
					className="flex-1 min-w-0"
					content={t(category.category)}
					disabled={!isLabelTruncated}
					position="top"
					align="start"
					delay={400}
				>
					<button
						type="button"
						onClick={() => {
							onSelect(category.category);
						}}
						className={`w-full min-w-0 text-left px-2 py-2 rounded-lg text-sm transition-colors flex items-center ${
							isSelected
								? 'bg-portal-accent/10 text-portal-accent font-medium'
								: 'text-strong hover:bg-sunken'
						}`}
					>
						<span className="flex items-center gap-2 min-w-0">
							{/*
							 * Category imagery is shown when an admin uploaded
							 * one. There is deliberately NO placeholder for the
							 * ones without: the amber swatch that used to fill
							 * the slot carried no information and cost 32px of
							 * the name's width on every row (PD-15471).
							 */}
							{hasImage && (
								<ResilientImage
									src={category.imageUrl}
									alt=""
									aria-hidden="true"
									className="w-6 h-6 shrink-0 rounded object-cover bg-brand-input"
									loading="lazy"
									onLikelyStaleUrl={() => {
										void queryClient.invalidateQueries({
											queryKey: ['catalog', 'categories'],
										});
									}}
								/>
							)}
							{/*
							 * No item count on the row. The sidebar is 224px, and any
							 * count treatment costs the name width: a right-aligned
							 * badge column takes ~35px from every row whether or not
							 * that row needs it, and an inline count gets clipped by
							 * the very names it was pushing out. Since the point of
							 * PD-15471 is that shoppers could not read the category
							 * names, the name gets the whole row.
							 */}
							<span ref={labelRef} className="truncate">
								{t(category.category)}
							</span>
						</span>
					</button>
				</Tooltip>
				{/*
				 * Disclosure chevron OUTSIDE the select button (nested buttons
				 * are invalid HTML): expands/collapses the subcategory list
				 * without changing the selection, so shoppers can peek into a
				 * category before committing. Categories without subcategories
				 * get a same-width spacer so every name ends at the same edge.
				 */}
				{category.subcategories.length > 0 ? (
					<button
						type="button"
						onClick={onToggleExpanded}
						className="w-5 h-7 shrink-0 flex items-center justify-center rounded text-brand-muted hover:bg-sunken"
						aria-expanded={subcategoriesShown}
						aria-label={
							subcategoriesShown
								? `${t('Collapse')} ${t(category.category)}`
								: `${t('Expand')} ${t(category.category)}`
						}
					>
						<FontAwesomeIcon
							icon={subcategoriesShown ? faChevronDown : faChevronRight}
							className="text-[10px]"
						/>
					</button>
				) : (
					<span className="w-5 shrink-0" aria-hidden="true" />
				)}
			</div>

			{subcategoriesShown && category.subcategories.length > 0 && (
				<div className="-mx-2 mt-1 space-y-0.5">
					{category.subcategories.map((sub) => (
						<button
							key={sub}
							type="button"
							onClick={() => {
								onSelect(category.category, sub);
							}}
							className={`w-full text-left pl-4 pr-2 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-1.5 ${
								selectedSubcategory === sub && isSelectedBranch
									? 'bg-portal-accent/10 text-portal-accent font-medium'
									: 'text-brand-muted hover:bg-sunken'
							}`}
						>
							<FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
							<span className="truncate">{t(sub)}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
